<svelte:options runes={true} />

<script lang="ts">
  import IconX from "~icons/dusk/x";
  import Badge from "~/components/ui/Badge/Badge.svelte";
  import type { Filter, FilterState } from "~/lib/filters/types";

  type FilterChip =
    | { kind: "checkbox"; filterId: string; value: string; label: string }
    | { kind: "range"; filterId: string; label: string };

  interface Props {
    filters: Filter[];
    activeFilters: FilterState;
    currencySymbol?: string;
    onRemove?: (filterId: string, value?: string) => void;
    onReset?: () => void;
    class?: string;
  }

  let {
    filters,
    activeFilters,
    currencySymbol = "£",
    onRemove,
    onReset,
    class: className = "",
  }: Props = $props();

  const chips = $derived.by<FilterChip[]>(() => {
    const result: FilterChip[] = [];
    for (const filter of filters) {
      const val = activeFilters[filter.id];
      if (!val) continue;

      if (filter.type === "checkbox") {
        const selected = val as string[];
        for (const v of selected) {
          const label = filter.options.find((o) => o.value === v)?.label ?? v;
          result.push({ kind: "checkbox", filterId: filter.id, value: v, label });
        }
      } else {
        const [lo, hi] = val as [number, number];
        const loAdjusted = lo !== filter.min;
        const hiAdjusted = hi !== filter.max;
        if (!loAdjusted && !hiAdjusted) continue;

        let label: string;
        if (loAdjusted && hiAdjusted) {
          label = `${currencySymbol}${lo} – ${currencySymbol}${hi}`;
        } else if (loAdjusted) {
          label = `From ${currencySymbol}${lo}`;
        } else {
          label = `Up to ${currencySymbol}${hi}`;
        }
        result.push({ kind: "range", filterId: filter.id, label });
      }
    }
    return result;
  });

  const hasFilters = $derived(chips.length > 0);
</script>

<div class={["da-active-filters", !hasFilters && "da-active-filters--empty", className]}>
  <div class="da-active-filters__inner">
    <span class="da-active-filters__heading" aria-hidden="true">Filters</span>

    <div class="da-active-filters__badges" role="group" aria-label="Active filters">
      {#each chips as chip (`${chip.filterId}-${chip.kind === "checkbox" ? chip.value : "range"}`)}
        <Badge
          as="span"
          size="sm"
          color="charcoal"
          icon="x-close"
          text={chip.label}
          class="da-active-filters__badge"
          onIconClick={() =>
            onRemove?.(chip.filterId, chip.kind === "checkbox" ? chip.value : undefined)}
        />
      {/each}
    </div>

    <button
      class="da-active-filters__reset"
      type="button"
      aria-label="Reset all filters"
      onclick={() => onReset?.()}
    >
      <span aria-hidden="true">Reset</span>
      <IconX aria-hidden="true" width="16" height="16" />
    </button>
  </div>
</div>

<style>
  @layer aurora {
    .da-active-filters__inner {
      display: flex;
      align-items: center;
      gap: var(--da-space-3);
      padding-inline-start: var(--da-space-4);
      padding-inline-end: 0;
      padding-block: var(--da-space-3);
      background: var(--da-color-brand-sand-700);
      overflow-x: auto;
      scrollbar-width: none;
    }

    .da-active-filters__inner::-webkit-scrollbar {
      display: none;
    }

    .da-active-filters--empty .da-active-filters__inner {
      height: 0;
      padding-block: 0;
      overflow: hidden;
    }

    .da-active-filters__heading {
      font-family: var(--da-font-family-primary);
      font-size: var(--da-font-size-xs);
      font-weight: var(--da-font-weight-semibold);
      line-height: var(--da-line-height-xs);
      letter-spacing: var(--da-letter-spacing-display);
      text-transform: uppercase;
      color: var(--da-color-charcoal-500);
      white-space: nowrap;
      flex-shrink: 0;
      display: none;
    }

    .da-active-filters__badges {
      display: flex;
      align-items: center;
      gap: var(--da-space-2);
      flex-shrink: 0;
      flex-wrap: nowrap;
      padding-inline-end: var(--da-space-4);
    }

    :global(.da-active-filters__badge) {
      box-shadow: var(--da-shadow-md);
      flex-shrink: 0;
    }

    .da-active-filters__reset {
      display: none;
      align-items: center;
      gap: var(--da-space-2);
      font-family: var(--da-font-family-primary);
      font-size: var(--da-font-size-sm);
      font-weight: var(--da-font-weight-semibold);
      letter-spacing: var(--da-letter-spacing-display);
      text-transform: uppercase;
      color: var(--da-color-charcoal-500);
      background: none;
      border: none;
      cursor: pointer;
      padding: 0;
      flex-shrink: 0;
      transition: opacity var(--da-duration-fast) ease;
    }

    .da-active-filters__reset:hover {
      opacity: 0.7;
    }

    .da-active-filters__reset:focus-visible {
      outline: 2px solid var(--da-color-charcoal-500);
      outline-offset: 2px;
      border-radius: var(--da-radius-sm);
    }

    @container (width >= 500px) {
      .da-active-filters__inner {
        margin-inline: calc(50% - 50vw);
        padding-inline: calc(50vw - 50% + var(--da-space-8));
        overflow-x: visible;
        gap: var(--da-space-3);
      }

      .da-active-filters__heading {
        display: block;
      }

      .da-active-filters__badges {
        flex: 1 0 0;
        min-width: 0;
        flex-wrap: wrap;
        gap: var(--da-space-3);
        padding-inline-end: 0;
      }

      .da-active-filters__reset {
        display: flex;
      }
    }
  }
</style>
